import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { getSkippedArchiveFiles } from 'DataDiscovery/discoverApi';

const SkippedArchiveFilesDialog = ({ show, onHide, runId, unprocessedItems }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    if (!show || !runId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSkippedArchiveFiles(runId)
      .then((data) => {
        if (cancelled) return;
        setEntries(Array.isArray(data?.entries) ? data.entries : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e && e.message ? e.message : 'Failed to load skipped files.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [show, runId]);

  const unsupportedRows = Array.isArray(unprocessedItems) ? unprocessedItems : [];

  return (
    <Modal show={show} onHide={onHide} size='lg' scrollable backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontFamily: 'Rubik', fontSize: 18 }}>
          Files skipped during scan
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '60vh' }}>
        {unsupportedRows.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <Typography
              variant='subtitle2'
              style={{ fontFamily: 'Rubik', fontWeight: 600, marginBottom: 8 }}
            >
              Unsupported file types
            </Typography>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 600 }}>File type</TableCell>
                    <TableCell style={{ fontWeight: 600, width: 120 }} align='right'>Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unsupportedRows.map((row) => (
                    <TableRow key={row.label} hover>
                      <TableCell style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {row.label}
                      </TableCell>
                      <TableCell align='right' style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {Number(row.count).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        <Typography
          variant='subtitle2'
          style={{ fontFamily: 'Rubik', fontWeight: 600, marginBottom: 8 }}
        >
          Archive errors
        </Typography>
        <Typography
          variant='body2'
          color='textSecondary'
          style={{ fontFamily: 'Rubik', marginBottom: 12 }}
        >
          Archive files or inner entries the scanner could not analyse.
        </Typography>
        {loading && (
          <div className='d-flex justify-content-center my-4'>
            <Spinner animation='border' variant='primary' />
          </div>
        )}
        {error && (
          <Alert variant='danger' style={{ fontFamily: 'Rubik', fontSize: 14 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && entries.length === 0 && (
          <Typography variant='body2' style={{ fontFamily: 'Rubik' }}>
            No archive files were skipped.
          </Typography>
        )}
        {!loading && !error && entries.length > 0 && (
          <TableContainer>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Extension</TableCell>
                  <TableCell style={{ fontWeight: 600 }}>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((row, i) => (
                  <TableRow key={`${row.filePath}-${i}`} hover>
                    <TableCell style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                      {row.filePath}
                    </TableCell>
                    <TableCell style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {row.extension ? `.${row.extension}` : '—'}
                    </TableCell>
                    <TableCell style={{ fontSize: 13 }}>
                      {row.reasonText || row.skipReason}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SkippedArchiveFilesDialog;
